<template>
  <div class='insights-layout'>
    <div class="insights-filters-header">
      <div class="domain-select">
        <advanced-filters-header
          layout='insights'
          @applyDomains='apply'
          @applyOrganizations='() => {}'
        />
      </div>
      <div class='insights-filters-header__container'>
        <div class='insights-filters-header__container__wrapper'>
          <company-logo :domain="whiteLabelingConfig.domain" view="ai" v-if="whiteLabelingConfig.domain"/>
          <img :src="activeAiLogo" class='insights-filters-header__container__logo' v-else/>
          <div class='insights-filters-header__container__text'>Powered by</div>
        </div>
      </div>
    </div>
    <div class='insights-list-content' :class='{ loading: insightsLoading }'>
      <v-card flat tile>
        <v-card-actions>
          <div class='insights-list-content__actions-row'>
            <div class='insights-list-content__actions-row__title'>
              {{this.currentDomainName}}
              <div class='insights-list-content__actions-row__title__subtitle'>
                {{this.currentDataDateFormatted}}
              </div>
            </div>
            <div v-if='Object.keys(this.insights).length > 1' class='insights-list-content__actions-row__actions'>
              <div @mouseenter='animationMouseEnter("leftArrowAnimation")' class='control-animation-wrapper' @click='prev'>
                <lottie-animation
                  class='control-animation'
                  :loop='true'
                  :animationData="isDarkMode ? require('@/assets/icons/animations/arrow-left-white.json') : require('@/assets/icons/animations/arrow-left.json')"
                  ref="leftArrowAnimation"
                  @loopComplete="loopComplete('leftArrowAnimation')"
                />
              </div>
              <v-item-group
                v-model="currentDomain"
                class="text-center"
                mandatory
              >
                <v-item
                  v-for="n in Object.keys(this.insights).length"
                  :key="`btn-${n}`"
                  v-slot="{ active }"
                >
                  <v-btn icon :input-value="active"  @click="toggle(n)" class='switch-insights-control-button-rounded'>
                    <v-icon x-small :color="hasData(n)">circle</v-icon>
                  </v-btn>
                </v-item>
              </v-item-group>
              <div @mouseenter='animationMouseEnter("rightArrowAnimation")' class='control-animation-wrapper' @click='next'>
                <lottie-animation
                  class='control-animation'
                  :loop='true'
                  :animationData="isDarkMode ? require('@/assets/icons/animations/arrow-right-white.json') : require('@/assets/icons/animations/arrow-right.json')"
                  ref="rightArrowAnimation"
                  @loopComplete="loopComplete('rightArrowAnimation')"
                />
              </div>
            </div>
          </div>
        </v-card-actions>
        <v-window v-model="currentDomain">
          <v-window-item v-for='(domain, index) in Object.keys(insights)' :key='index'>
              <domain-insights
                :domain='domain'
                :data='insights[domain]'
                :max-width='dynamicScrollContainerSize'
                :asset-correlation='getAssetCorrelationForTld(domain)'
                :is-loading='insightsLoading'
              />
          </v-window-item>
        </v-window>
      </v-card>
      <page-loading-overlay :show="insightsLoading" contained />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS,
  LOAD_DOMAINS_FROM_CONFIGURATION,
  SET_LOADING_DATA_FLAG,
} from '@/store/project/actionTypes';
import { LOAD_INSIGHTS } from '@/store/insights/actionTypes';
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import DomainInsights from '@/components/insights/DomainInsights.vue';
import { LOAD_DOMAINS } from '@/store/user/actionTypes';
import CynergyAppAi from '@/assets/icons/whitelabeling/cynergy.app/cynai-logo-black.svg';
import CynergyAppAiWhite from '@/assets/icons/whitelabeling/cynergy.app/cynai-logo-white.svg';
import LottieAnimation from 'lottie-web-vue';
import { CLEAR_DOMAIN_FILTER } from '@/store/filters/actionTypes';
import { CLEAR_INSIGHTS, CLEAR_ASSET_CORRELATION, SET_INSIGHTS_LOADING } from '@/store/insights/mutationTypes';
import AdvancedFiltersHeader from '@/components/common/AdvancedFiltersHeader.vue';
import CompanyLogo from '@/components/common/CompanyLogo.vue';

export default {
  name: 'InsightsLayout',
  components: {
    AdvancedFiltersHeader,
    DomainInsights,
    PageLoadingOverlay,
    LottieAnimation,
    CompanyLogo,
  },
  data() {
    return {
      currentDomain: 0,
      currentDomainName: '',
      currentDataDate: '',
      windowInnerWidth: window.innerWidth,
      CynergyAppAi,
      CynergyAppAiWhite,
    };
  },
  watch: {
    async organizationFilter() {
      await this.$store.dispatch(CLEAR_DOMAIN_FILTER);
    },
    async profile() {
      if (this.profile) {
        this.$store.commit(SET_INSIGHTS_LOADING, true);
        await this.$store.dispatch(LOAD_DOMAINS);
        await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
        await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
          noLimit: true,
        });
        await this.loadInsights();
        this.currentDomain = 0;
        this.currentDomainName = Object.keys(this.insights)[this.currentDomain];
        this.currentDataDate = this.insights[this.currentDomainName].date;
      }
    },
  },
  async created() {
    if (this.profile) {
      // Show loading immediately before any async work begins
      this.$store.commit(SET_INSIGHTS_LOADING, true);
      await this.$store.dispatch(LOAD_DOMAINS);
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
      await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
        noLimit: true,
      });
      await this.loadInsights();
      this.currentDomain = 0;
      this.currentDomainName = Object.keys(this.insights)[this.currentDomain];
      this.currentDataDate = this.insights[this.currentDomainName].date;
    }
    window.addEventListener('resize', this.windowResizeHandler);
  },
  destroyed() {
    window.removeEventListener('resize', this.windowResizeHandler);
    this.$store.commit(CLEAR_INSIGHTS);
    this.$store.commit(CLEAR_ASSET_CORRELATION);
  },
  computed: {
    ...mapGetters([
      'domainFilter',
      'configurationDomains',
      'domains',
      'insights',
      'insightsLoading',
      'profile',
      'whiteLabelingConfig',
      'organizationFilter',
      'assetCorrelation',
      'getAssetCorrelationForTld',
    ]),
    ...mapGetters('theme', ['isDarkMode']),
    activeAiLogo() {
      return this.isDarkMode ? this.CynergyAppAiWhite : this.CynergyAppAi;
    },
    currentDataDateFormatted() {
      return this.currentDataDate ? new Date(this.currentDataDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }) : this.currentDataDate;
    },
    listOfDomains() {
      const domainsSet = new Set(this.domains);
      this.configurationDomains.forEach(configurationDomain => domainsSet.add(configurationDomain.configData));
      return Array.from(domainsSet);
    },
    domainSelected: {
      get() {
        return this.domainFilter;
      },
    },
    dynamicScrollContainerSize() {
      const dividers = (this.windowInnerWidth - 120) * 0.04;
      return Math.floor((this.windowInnerWidth - 130 - dividers));
    },
  },
  methods: {
    hasData(domain) {
      const domainKey = Object.keys(this.insights)[domain - 1];
      const domainData = this.insights[domainKey]?.data;

      if (!domainData) return '';

      const insightsRoot = domainData?.insights?.insightsData?.insights || {};
      const dnsTwistData = domainData?.dnsTwist?.dnsTwistData || {};

      // Detect version
      let version = 0;
      if (insightsRoot?.estimated_percentage_of_getting_hacked) version = 1;
      else if (insightsRoot?.summary) version = 2;

      let hasInsights = false;

      // ---- Version 1 logic (with correct computed APT logic) ----
      if (version === 1) {
        const aptGroupsRaw = insightsRoot?.relevant_APT_groups;
        const aptGroups =
          aptGroupsRaw && aptGroupsRaw !== 'N/A'
            ? aptGroupsRaw
            : [];

        hasInsights =
          Object.keys(insightsRoot?.estimated_percentage_of_getting_hacked || {}).length > 0 &&
          aptGroups.length > 0;
      } else if (version === 2) {
        const aptGroupsRaw = insightsRoot?.relevant_APT_groups;
        const aptGroups =
          aptGroupsRaw && aptGroupsRaw !== 'N/A'
            ? aptGroupsRaw
            : [];

        hasInsights =
          aptGroups.length > 0 &&
          (insightsRoot?.threat_actors_view || []).length > 0;
      }

      // ---- DNS Twist fallback ----
      const hasDNSTwist = Object.keys(dnsTwistData || {}).length > 0;
      return (hasInsights || hasDNSTwist) ? '#2baf69' : '';
    },
    windowResizeHandler() {
      this.windowInnerWidth = window.innerWidth;
    },
    animationMouseEnter(reference) {
      this.$refs[reference].play();
    },
    loopComplete(reference) {
      this.$refs[reference].pause();
    },
    async apply() {
      if (this.domainSelected.length > 0) {
        await this.$store.dispatch(SET_LOADING_DATA_FLAG, true);
        await this.loadInsights();
        this.currentDomain = 0;
        this.currentDomainName = Object.keys(this.insights)[this.currentDomain];
        this.currentDataDate = this.insights[this.currentDomainName].date;
      }
    },
    async loadInsights() {
      await this.$store.dispatch(LOAD_INSIGHTS, {tlds: this.domainSelected});
    },
    dispatch(e) {
      this.$emit('input', e);
      this.domainSelected = e;
    },
    next() {
      this.currentDomain = this.currentDomain + 1 === Object.keys(this.insights).length
        ? 0
        : this.currentDomain + 1;
      this.currentDomainName = Object.keys(this.insights)[this.currentDomain];
      this.currentDataDate = this.insights[this.currentDomainName].date;
    },
    prev() {
      this.currentDomain = this.currentDomain - 1 < 0
        ? Object.keys(this.insights).length - 1
        : this.currentDomain - 1;
      this.currentDomainName = Object.keys(this.insights)[this.currentDomain];
      this.currentDataDate = this.insights[this.currentDomainName].date;
    },
    toggle(index) {
      this.currentDomain = index - 1;
      this.currentDomainName = Object.keys(this.insights)[this.currentDomain];
      this.currentDataDate = this.insights[this.currentDomainName].date;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.insights-layout {
  .domain-select {
    margin-top: $space-2;
    margin-left: $space-8;
    margin-bottom: $space-4;
  }
  .insights-filters-header {
    display: flex;
    flex-direction: row;
    margin-bottom: $space-2;
    justify-content: space-between;
    &__container{
      height: rem(50);
      display: flex;
      flex-direction: row;
      margin-right: $space-8;
      align-items: center;
      justify-content: flex-end;
      &__wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      &__logo{
        width: rem(120);
        height: auto;
        margin-top: $space-8;
      }
      &__text{
        font-family: $font-family-semibold;
        font-size: rem(8);
        color: var(--text-secondary);
      }
    }
  }
  .insights-list-content {
    position: relative; // Required for contained loading overlay
    min-height: calc(100vh - 150px); // Ensure overlay covers content area

    &__actions-row{
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      width: 100%;
      margin: 0 $space-5;
      &__title{
        text-transform: uppercase;
        margin-bottom: $space-3;
        color: var(--text-primary);
        font-family: $font-family-bold;
        font-size: $font-size-xl;
        font-weight: bold;
        line-height: rem(18);
        letter-spacing: 0;
        &__subtitle{
          font-family: $font-family-regular;
          font-size: $font-size-xs;
          font-weight: normal;
          line-height: rem(16);
          letter-spacing: 0;
        }
      }
      &__actions{
        display: flex;
        flex-direction: row;
        align-items: center;
      }
      &__beta {
        margin-top: rem(50);
        position: absolute;
        &:hover {
          cursor: pointer;
        }
        &__text {
          padding-left: $space-1;
          font-size: $font-size-sm;
        }
        &__icon {
          font-size: $font-size-xl;
          margin-top: rem(-2);
          color: var(--severity-critical);
        }
      }
    }
    .control-animation-wrapper{
      &:hover {
        cursor: pointer;
      }
    }
    .control-animation{
      height: rem(28);
    }
  }
  .switch-insights-control-button-rounded {
    border-radius: 50% !important;
    height: rem(24) !important;
    width: rem(24);

    &.v-item--active {
      color: var(--brand-primary) !important;

      ::v-deep .v-btn__content {
        i.v-icon {
          color: var(--brand-primary) !important;
        }
      }
    }
  }
}
</style>

<style lang='scss'>
// Unscoped: override vuetify-overrides .v-card.v-card background for insights page
.insights-layout {
  .insights-list-content {
    > .v-card.v-card {
      background: #ffffff;
    }
  }
}

[data-theme="dark"] {
  .insights-layout {
    .insights-list-content {
      > .v-card.v-card {
        background: var(--bg-secondary);
      }
    }
  }
}
</style>
